import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Building2, ShieldCheck, Sparkles } from "lucide-react";
import { useDocstate } from "@/context/DocstateContext";
import { HeroSearch } from "@/components/docstate/HeroSearch";
import { PropertyCard } from "@/components/docstate/PropertyCard";
import { reListings, dl } from "@/lib/docstate";

const HERO_BG = "https://images.pexels.com/photos/19407884/pexels-photo-19407884.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920";

const COMMUNITY_IMG = {
  "Dubai Marina": "https://images.unsplash.com/photo-1654520015092-b9b4ddf2e7c7?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "Palm Jumeirah": "https://images.unsplash.com/photo-1782720829237-ec146b0afe0a?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "Downtown Dubai": "https://images.pexels.com/photos/40134484/pexels-photo-40134484.jpeg?auto=compress&cs=tinysrgb&w=800",
  "Business Bay": "https://images.unsplash.com/photo-1735748725537-2e6f48d2eb0b?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "Dubai Hills Estate": "https://images.unsplash.com/photo-1743819455744-05417bf55cea?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "Arabian Ranches": "https://images.pexels.com/photos/10647324/pexels-photo-10647324.jpeg?auto=compress&cs=tinysrgb&w=800",
};
const FALLBACK_IMG = "https://images.unsplash.com/photo-1719474815671-08411b0ea840?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

const WHY = [
  { icon: ShieldCheck, title: "RERA-Verified", text: "Every listing carries a DLD permit and RERA-registered agents — full transparency, zero surprises." },
  { icon: Sparkles, title: "Hand-Curated", text: "A tightly edited portfolio of Dubai's finest residences and investment-grade off-plan releases." },
  { icon: Building2, title: "Developer Access", text: "Direct relationships with Emaar, Nakheel, DAMAC and Select Group for priority allocations." },
];

export default function DocstateHome() {
  const data = useDocstate();
  const s = data?.settings || {};
  const communities = data?.communities || [];
  const agents = data?.agents || [];
  const [featured, setFeatured] = useState([]);
  const trackRef = useRef(null);

  useEffect(() => {
    reListings({ featured: "1", limit: 8 })
      .then((d) => (d.items.length ? setFeatured(d.items) : reListings({ limit: 8 }).then((x) => setFeatured(x.items))))
      .catch(() => setFeatured([]));
  }, []);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  const list = featured || [];

  return (
    <div data-testid="docstate-home">
      {/* HERO */}
      <section className="ds-hero relative flex min-h-[92vh] items-center overflow-hidden">
        <motion.img
          src={s.hero_image || HERO_BG}
          alt="Dubai skyline"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 18, ease: "linear" }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/80 via-[#0B0B0C]/55 to-[#0B0B0C]" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pt-28 sm:px-8 lg:px-12">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="ds-eyebrow">
            {s.tagline || "Doctor of Real Estate · Dubai"}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="ds-display mt-4 max-w-3xl text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-6xl"
          >
            {s.hero_title || "Dubai's Finest Addresses, Expertly Matched"}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="mt-4 max-w-xl text-base text-[#C9BBB2]">
            {s.hero_subtitle || "Curated residences and investments across the city's most sought-after communities."}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="mt-8 max-w-4xl">
            <HeroSearch />
          </motion.div>
        </div>
      </section>

      {/* FEATURED CAROUSEL */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="ds-eyebrow">Featured Residences</p>
            <h2 className="ds-display mt-2 text-2xl font-medium text-white sm:text-3xl lg:text-4xl">Signature Listings</h2>
          </div>
          <div className="hidden gap-2 sm:flex">
            <button onClick={() => scrollBy(-1)} data-testid="featured-prev" aria-label="Previous" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#C9A24B]/30 text-[#F3E5AB] transition-colors hover:bg-[#C9A24B]/10"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => scrollBy(1)} data-testid="featured-next" aria-label="Next" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#C9A24B]/30 text-[#F3E5AB] transition-colors hover:bg-[#C9A24B]/10"><ChevronRight className="h-5 w-5" /></button>
          </div>
        </div>
        <div ref={trackRef} className="mt-8 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="featured-track">
          {list.map((l, i) => (
            <div key={l.id} className="w-[85%] shrink-0 snap-start sm:w-[46%] lg:w-[31%]">
              <PropertyCard listing={l} index={i} />
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-[#8A7A6F]">Listings are being curated.</p>}
        </div>
        <div className="mt-6 text-center">
          <Link to={dl("/properties")} data-testid="home-view-all" className="ds-outline-btn px-7 py-3 text-xs">View All Properties <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* COMMUNITIES */}
      {communities.length > 0 && (
        <section className="border-t border-white/5 bg-[#09090A]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
            <p className="ds-eyebrow">Browse by Community</p>
            <h2 className="ds-display mt-2 text-2xl font-medium text-white sm:text-3xl lg:text-4xl">Dubai's Prime Addresses</h2>
            <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:gap-6">
              {communities.map((c) => (
                <Link
                  key={c}
                  to={dl(`/properties?community=${encodeURIComponent(c)}`)}
                  data-testid={`community-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group relative aspect-[3/2] overflow-hidden rounded-xl border border-[#C9A24B]/15"
                >
                  <img src={COMMUNITY_IMG[c] || FALLBACK_IMG} alt={c} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C]/90 via-transparent to-transparent" />
                  <p className="ds-display absolute bottom-3 left-4 text-lg font-semibold text-white">{c}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY DOCSTATE */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <div className="grid gap-6 md:grid-cols-3">
          {WHY.map((w, i) => (
            <motion.div key={w.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="ds-card p-7">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C9A24B]/12 text-[#C9A24B]"><w.icon className="h-6 w-6" /></span>
              <h3 className="ds-display mt-5 text-xl font-semibold text-white">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#9D8D83]">{w.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AGENTS PREVIEW */}
      {agents.length > 0 && (
        <section className="border-t border-white/5 bg-[#09090A]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
            <div className="flex items-end justify-between">
              <div>
                <p className="ds-eyebrow">Meet the Team</p>
                <h2 className="ds-display mt-2 text-2xl font-medium text-white sm:text-3xl lg:text-4xl">Your Dubai Advisors</h2>
              </div>
              <Link to={dl("/agents")} className="ds-outline-btn hidden px-6 py-2.5 text-xs sm:inline-flex">All Agents</Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-5 lg:grid-cols-4">
              {agents.slice(0, 4).map((a) => (
                <div key={a.id} className="ds-card overflow-hidden">
                  <div className="aspect-[3/4] overflow-hidden">
                    {a.photo ? <img src={a.photo} alt={a.name} loading="lazy" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-[#121214]" />}
                  </div>
                  <div className="p-4">
                    <p className="ds-display text-base font-semibold text-white">{a.name}</p>
                    <p className="mt-0.5 text-xs text-[#C9A24B]">{a.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-[#C9A24B]/20 bg-gradient-to-br from-[#121214] to-[#0B0B0C]">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-8 lg:px-12 lg:py-24">
          <h2 className="ds-display mx-auto max-w-2xl text-3xl font-medium text-white lg:text-4xl">Thinking of selling or leasing your property?</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-[#C9BBB2]">List with Docstate and reach qualified buyers and tenants across Dubai.</p>
          <Link to={dl("/list-with-us")} data-testid="home-cta-list" className="ds-gold-btn mt-7 px-8 py-3.5 text-sm">List Your Property <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
}
